import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('aea_token');
    if (!token) { setLoading(false); return; }
    try {
      const response = await authApi.getMe();
      setUser(response.data);
    } catch {
      localStorage.removeItem('aea_token');
      localStorage.removeItem('aea_user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authApi.login({ email, password });
      // Access token en respuesta; refresh token en cookie HttpOnly (no visible desde JS).
      const { access_token, token, user: userData, require_password_change } = response.data;
      const at = access_token || token;
      localStorage.setItem('aea_token', at);
      localStorage.setItem('aea_user', JSON.stringify(userData));
      setUser(userData);
      setRequirePasswordChange(!!require_password_change);
      return { user: userData, requirePasswordChange: !!require_password_change };
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al iniciar sesión';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (userData) => {
    setError(null);
    try {
      const response = await authApi.register(userData);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Error en el registro';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try { await authApi.logout(); } catch { /* server-side revoke best-effort */ }
    localStorage.removeItem('aea_token');
    localStorage.removeItem('aea_user');
    setUser(null);
    setRequirePasswordChange(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    await authApi.changePassword(currentPassword, newPassword);
    setRequirePasswordChange(false);
  };

  const refreshUser = async () => { await loadUser(); };

  const value = {
    user, loading, error, login, register, logout, refreshUser,
    changePassword, requirePasswordChange,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin_aea',
    isEmpresa: user?.role === 'empresa',
    isAuditor: user?.role === 'auditor',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
